import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { chatReducer } from './chatReducer';
import {
  initialChatState,
  ChatState,
  ChatAction,
  ConversationData,
  ChatMessage,
} from './chatTypes';
import { useAuth } from '../../web3/context/AuthContext';
import { createSigner, getChatAddress } from '../signers/signerFactory';
import { Client, ConsentState, IdentifierKind, SortDirection } from '@xmtp/browser-sdk';
import {
  initializeXmtpClient,
  buildXmtpClient,
  createXmtpClient,
  closeXmtpClient,
  forceResetXmtpClient,
  acquireTabLock,
  releaseTabLock,
  requestTabTakeover,
  listenForTakeover,
  canMessage,
  InstallationLimitError,
  registerAfterRevocation,
  type InitResult,
} from '../services/xmtpClient';
import { backupXmtpDatabase, ensureFirebaseBackup, restoreXmtpDatabase, hasFirebaseBackup } from '../services/xmtpBackup';
import { syncMessageArchive, mergeArchiveIntoState, restoreConversationMessages, downloadManifest, invalidateManifestCache } from '../services/messageArchive';
import { ensureFirebaseAuth, trySilentFirebaseAuth, setStoredSigner } from '../services/firebaseStorage';
import { ensureDbKeyFromFirebase } from '../services/xmtpClient';
import type { RemoteAttachmentMetadata } from '../services/attachmentService';
import toast from 'react-hot-toast';
import { reverseHnsLookup } from '../../../api/mcpApi';

const XMTP_ENV = process.env.REACT_APP_NETWORK_TYPE === 'TESTNET' ? 'dev' : 'production';

const ATTACHMENT_PREFIX = '__horus_attachment__';
const PAYMENT_PREFIX = '__horus_payment__';
const PAYMENT_REQUEST_PREFIX = '__horus_payment_request__';

// Include Unknown + Allowed conversations in all list/stream calls.
// Without this, the XMTP SDK defaults to only Allowed conversations,
// hiding new DMs from unknown senders (ConsentState.Unknown).
const LIST_ALL_CONSENT: any = { consentStates: [ConsentState.Unknown, ConsentState.Allowed] };
const SYNC_ALL_CONSENT = [ConsentState.Unknown, ConsentState.Allowed];

// In-memory cache for inbox ID → Ethereum address resolution.
// Avoids redundant network calls to client.preferences.getLatestInboxState
// for the same inbox IDs during sync (e.g. user's own ID, recurring group members).
const inboxIdAddressCache = new Map<string, string>();

// ---- Context interfaces ----

interface ChatClientContextValue {
  isInitialized: boolean;
  isInitializing: boolean;
  initError: string | null;
  initProgress: string | null;
  hasCloudBackup: boolean | null;
  chatAddress: string | null;
  initializeChat: (password?: string) => Promise<void>;
  resetChat: (password?: string) => Promise<void>;
  showInstallationManager: boolean;
  pendingClient: any;
  pendingInboxId: string | null;
  completeRegistration: () => Promise<void>;
  dismissInstallationManager: () => void;
  revokeInstallations: (installationBytes: Uint8Array[], revokeAll: boolean) => Promise<void>;
  forceRevokeAllAndReset: (password?: string) => Promise<void>;
}

interface ChatDataContextValue {
  conversations: ConversationData[];
  activeConversationId: string | null;
  messages: Record<string, ChatMessage[]>;
  loadingMessages: Record<string, boolean>;
  hasMoreMessages: Record<string, boolean>;
  unreadCounts: Record<string, number>;
  totalUnread: number;
  searchQuery: string;
  isSyncing: boolean;
  conversationFilter: 'all' | 'dms' | 'groups';
  dispatch: React.Dispatch<ChatAction>;
  lastReadAt: number; // timestamp snapshot from before conversation was marked as read
  sendMessage: (conversationId: string, text: string, attachment?: RemoteAttachmentMetadata) => Promise<void>;
  loadMessages: (conversationId: string) => Promise<void>;
  loadOlderMessages: (conversationId: string) => Promise<void>;
  selectConversation: (conversationId: string | null) => void;
  createDm: (peerAddress: string) => Promise<string | null>;
  createGroup: (memberAddresses: string[], name?: string) => Promise<string | null>;
  addGroupMembers: (conversationId: string, addresses: string[]) => Promise<void>;
  removeGroupMember: (conversationId: string, address: string) => Promise<void>;
  loadGroupMembers: (conversationId: string) => Promise<void>;
  isGroupAdmin: (conversationId: string) => Promise<boolean>;
  syncConversations: () => Promise<void>;
}

const ChatClientContext = createContext<ChatClientContextValue>({
  isInitialized: false,
  isInitializing: false,
  initError: null,
  initProgress: null,
  hasCloudBackup: null,
  chatAddress: null,
  initializeChat: async () => { },
  resetChat: async () => { },
  showInstallationManager: false,
  pendingClient: null,
  pendingInboxId: null,
  completeRegistration: async () => { },
  dismissInstallationManager: () => { },
  revokeInstallations: async () => { },
  forceRevokeAllAndReset: async () => { },
});

const ChatDataContext = createContext<ChatDataContextValue>({
  conversations: [],
  activeConversationId: null,
  messages: {},
  loadingMessages: {},
  hasMoreMessages: {},
  unreadCounts: {},
  totalUnread: 0,
  searchQuery: '',
  isSyncing: false,
  conversationFilter: 'all',
  lastReadAt: 0,
  dispatch: () => { },
  sendMessage: async () => { },
  loadMessages: async () => { },
  loadOlderMessages: async () => { },
  selectConversation: () => { },
  createDm: async () => null,
  createGroup: async () => null,
  addGroupMembers: async () => { },
  removeGroupMember: async () => { },
  loadGroupMembers: async () => { },
  isGroupAdmin: async () => false,
  syncConversations: async () => { },
});

// ---- Last-read tracking for unread counts ----

const LAST_READ_PREFIX = 'horus-chat-lastread-';

function getLastReadTime(conversationId: string): number {
  const raw = localStorage.getItem(LAST_READ_PREFIX + conversationId);
  return raw ? parseInt(raw, 10) : 0;
}

function setLastReadTime(conversationId: string): void {
  localStorage.setItem(LAST_READ_PREFIX + conversationId, Date.now().toString());
}

// ---- Local cache for instant sidebar + message loading ----

const CONVO_CACHE_PREFIX = 'horus-chat-convos-';
const MSG_CACHE_PREFIX = 'horus-chat-msgs-';
const MSG_CACHE_LIMIT = 30; // max messages cached per conversation

function getCachedConversations(address: string): ConversationData[] | null {
  try {
    const raw = localStorage.getItem(CONVO_CACHE_PREFIX + address.toLowerCase());
    if (!raw) return null;
    const arr = JSON.parse(raw);
    // Revive Date fields
    return arr.map((c: any) => ({
      ...c,
      createdAt: new Date(c.createdAt),
      lastMessageTime: c.lastMessageTime ? new Date(c.lastMessageTime) : undefined,
    }));
  } catch { return null; }
}

function setCachedConversations(address: string, conversations: ConversationData[]): void {
  try {
    localStorage.setItem(CONVO_CACHE_PREFIX + address.toLowerCase(), JSON.stringify(conversations));
  } catch (err) {
    console.warn('[Cache] Failed to save conversations:', err);
  }
}

function getCachedMessages(conversationId: string): ChatMessage[] | null {
  try {
    const key = MSG_CACHE_PREFIX + conversationId;
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const arr = JSON.parse(raw);
    return arr.map((m: any) => ({ ...m, sentAt: new Date(m.sentAt) }));
  } catch (err) {
    console.warn('[Cache] Failed to read messages for', conversationId, err);
    return null;
  }
}

function setCachedMessages(conversationId: string, messages: ChatMessage[]): void {
  try {
    // Only cache the most recent messages to avoid localStorage bloat
    const toCache = messages.slice(-MSG_CACHE_LIMIT);
    const key = MSG_CACHE_PREFIX + conversationId;
    localStorage.setItem(key, JSON.stringify(toCache));
  } catch (err) {
    console.warn('[Cache] Failed to save messages for', conversationId, err);
  }
}

/**
 * Merge two message arrays, deduplicating by ID and sorting by time.
 * Preserves messages from both sources (cache + network).
 */
function mergeMessages(a: ChatMessage[], b: ChatMessage[]): ChatMessage[] {
  const map = new Map<string, ChatMessage>();
  // Add a first, then b — b wins on duplicates (newer/resolved data)
  for (const m of a) map.set(m.id, m);
  for (const m of b) map.set(m.id, m);
  return Array.from(map.values()).sort(
    (x, y) => x.sentAt.getTime() - y.sentAt.getTime()
  );
}

/**
 * Update a single conversation's last-message preview in the localStorage cache.
 * Called after sending a message or receiving one via stream, so the sidebar
 * preview survives a page refresh without waiting for syncConversations.
 */
function updateConversationCachePreview(
  address: string,
  conversationId: string,
  text: string,
  time: Date,
  sender: string,
): void {
  const cached = getCachedConversations(address);
  if (!cached) return;
  const idx = cached.findIndex((c) => c.id === conversationId);
  if (idx === -1) return;
  cached[idx].lastMessageText = text;
  cached[idx].lastMessageTime = time;
  cached[idx].lastMessageSender = sender;
  // Re-sort by latest message time
  cached.sort((a, b) => {
    const tA = a.lastMessageTime?.getTime() || a.createdAt.getTime();
    const tB = b.lastMessageTime?.getTime() || b.createdAt.getTime();
    return tB - tA;
  });
  setCachedConversations(address, cached);
}

// ---- Helper to convert XMTP messages to ChatMessage ----

// XMTP internal content types that should not be rendered as user messages
const SYSTEM_CONTENT_TYPES = new Set([
  'group_membership_change',
  'group_updated',
]);

function isSystemContent(content: any): boolean {
  if (typeof content !== 'object' || content === null) return false;
  // XMTP group membership change messages have this shape
  return 'initiatedByInboxId' in content && 'addedInboxes' in content && 'removedInboxes' in content;
}

/**
 * Parse a timestamp from XMTP objects.
 * The browser-sdk uses nanosecond BigInt (sentAtNs, createdAtNs) as primary timestamps.
 * Some SDK versions also expose Date getters (sentAt, createdAt).
 */
function parseXmtpTimestamp(nsValue: any, dateValue: any): Date {
  // Prefer nanosecond BigInt — always present in browser-sdk
  if (nsValue != null) {
    try {
      return new Date(Number(BigInt(nsValue) / BigInt(1_000_000)));
    } catch {
      // Fallback if BigInt conversion fails
    }
  }
  // Date object (getter in some SDK versions)
  if (dateValue instanceof Date && !isNaN(dateValue.getTime())) {
    return dateValue;
  }
  // Millisecond number
  if (typeof dateValue === 'number' && dateValue > 0) {
    return new Date(dateValue);
  }
  return new Date();
}

function toMessage(msg: any, myInboxId: string): ChatMessage | null {
  const contentTypeId = msg.contentType?.typeId || '';

  // Skip XMTP internal/system messages
  if (SYSTEM_CONTENT_TYPES.has(contentTypeId) || isSystemContent(msg.content)) {
    return null;
  }

  const senderInboxId = msg.senderInboxId || '';
  let content = msg.content;
  let contentType = contentTypeId || 'text';

  // Detect Horus attachment messages (JSON with prefix)
  if (typeof content === 'string' && content.startsWith(ATTACHMENT_PREFIX)) {
    try {
      content = JSON.parse(content.slice(ATTACHMENT_PREFIX.length));
      contentType = 'remoteAttachment';
    } catch {
      // Not valid JSON, treat as text
    }
  }

  // Detect payment messages
  if (typeof content === 'string' && content.startsWith(PAYMENT_PREFIX) && !content.startsWith(PAYMENT_REQUEST_PREFIX)) {
    try {
      content = JSON.parse(content.slice(PAYMENT_PREFIX.length));
      contentType = 'payment';
    } catch { /* treat as text */ }
  }

  // Detect payment request messages
  if (typeof content === 'string' && content.startsWith(PAYMENT_REQUEST_PREFIX)) {
    try {
      content = JSON.parse(content.slice(PAYMENT_REQUEST_PREFIX.length));
      contentType = 'paymentRequest';
    } catch { /* treat as text */ }
  }

  // Skip messages with no displayable content
  if (content === undefined || content === null || content === '') {
    return null;
  }

  const sentAt = parseXmtpTimestamp(msg.sentAtNs, msg.sentAt);

  return {
    id: msg.id || `${sentAt.getTime()}-${senderInboxId}`,
    conversationId: msg.conversationId || '',
    senderAddress: senderInboxId,
    content,
    contentType,
    sentAt,
    // Compare inbox IDs — senderInboxId and client.inboxId are both XMTP inbox IDs
    isSentByMe: senderInboxId.toLowerCase() === myInboxId.toLowerCase(),
  };
}

function getMessagePreview(message: ChatMessage, rawMsg?: any): string {
  if (message.contentType === 'remoteAttachment' && message.content?.isVoiceNote) {
    return '\uD83C\uDFA4 Voice message';
  }
  if (message.contentType === 'payment') {
    return `\uD83D\uDCB0 ${message.content?.amount || ''} ${message.content?.tokenSymbol || ''}`.trim();
  }
  if (message.contentType === 'paymentRequest') {
    return `\uD83D\uDD14 Request: ${message.content?.amount || ''} ${message.content?.tokenSymbol || ''}`.trim();
  }
  if (typeof message.content === 'string') {
    return message.content.slice(0, 100);
  }
  return message.contentType === 'remoteAttachment'
    ? '[Attachment]'
    : (typeof rawMsg?.content === 'string' ? rawMsg.content.slice(0, 100) : '[Attachment]');
}

/**
 * Resolve an XMTP inbox ID to an Ethereum address via the client.
 */
async function resolveInboxIdToAddress(client: any, inboxId: string): Promise<string> {
  if (!inboxId) return '';

  const cached = inboxIdAddressCache.get(inboxId);
  if (cached) return cached;

  try {
    const states = await client.preferences.getInboxStates([inboxId]);
    const state = states?.[0];
    const ethId = state?.accountIdentifiers?.find(
      (id: any) => id.identifierKind === IdentifierKind.Ethereum
    );
    const address = ethId?.identifier || inboxId;
    inboxIdAddressCache.set(inboxId, address);
    return address;
  } catch {
    return inboxId;
  }
}

async function toConversation(convo: any, client: any): Promise<ConversationData> {
  // Fetch members — in the browser-sdk, members() is an async method
  let rawMembers: any[] = [];
  try {
    rawMembers = typeof convo.members === 'function'
      ? await convo.members()
      : Array.isArray(convo.members) ? convo.members : [];
  } catch {
    // Non-critical
  }

  const isGroup = rawMembers.length > 2 || !!convo.name;

  // Resolve member inbox IDs to Ethereum addresses
  let memberAddresses: string[] = [];
  if (isGroup && rawMembers.length > 0) {
    memberAddresses = await Promise.all(
      rawMembers.map(async (m: any) => {
        const inboxId = m.inboxId || m;
        return resolveInboxIdToAddress(client, inboxId);
      })
    );
  }

  // peerInboxId is an async method in the XMTP SDK, not a property
  let peerInboxId = '';
  if (!isGroup && typeof convo.peerInboxId === 'function') {
    try {
      peerInboxId = await convo.peerInboxId();
    } catch {
      peerInboxId = '';
    }
  }
  // Resolve inbox ID to Ethereum address for display
  const peerAddress = peerInboxId
    ? await resolveInboxIdToAddress(client, peerInboxId)
    : '';

  return {
    id: convo.id || convo.topic,
    topic: convo.topic || convo.id,
    peerAddress,
    peerInboxId: peerInboxId || undefined,
    isGroup,
    groupName: convo.name || undefined,
    members: memberAddresses,
    createdAt: parseXmtpTimestamp(convo.createdAtNs, convo.createdAt),
    lastMessageText: undefined,
    lastMessageTime: undefined,
  };
}

/**
 * Lightweight version of toConversation for fast initial sidebar render.
 * Resolves peer address for DMs but skips group member resolution (loaded on demand).
 */
async function toConversationLight(convo: any, client: any): Promise<ConversationData> {
  const isGroup = !!convo.name;

  let peerAddress = '';
  let peerInboxId = '';
  if (!isGroup && typeof convo.peerInboxId === 'function') {
    try {
      peerInboxId = await convo.peerInboxId();
      peerAddress = await resolveInboxIdToAddress(client, peerInboxId);
    } catch { /* non-critical */ }
  }

  return {
    id: convo.id || convo.topic,
    topic: convo.topic || convo.id,
    peerAddress,
    peerInboxId: peerInboxId || undefined,
    isGroup,
    groupName: convo.name || undefined,
    members: undefined,
    createdAt: parseXmtpTimestamp(convo.createdAtNs, convo.createdAt),
    lastMessageText: undefined,
    lastMessageTime: undefined,
  };
}

// ---- Provider ----

const MESSAGE_PAGE_SIZE = 30;
const ENRICH_BATCH_SIZE = 5;

export function ChatProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(chatReducer, initialChatState);
  const { isLoggedIn, loginOption, entityInfo, email, hasPassword, userWalletsData, setIsLoading } = useAuth();
  const streamRef = useRef<any>(null);
  const convoStreamRef = useRef<any>(null);
  const lastReadAtRef = useRef<number>(0);
  const pendingSignerRef = useRef<any>(null);
  const clientRef = useRef(state.client);
  const activeConvoRef = useRef<string | null>(state.activeConversationId);
  const autoInitAttemptedRef = useRef(false);
  const convoMapCacheRef = useRef<Map<string, any>>(new Map());
  const resetChatRef = useRef<() => Promise<void>>();
  const isNewRegistrationRef = useRef(false);
  const stateConvosRef = useRef(state.conversations);
  const stateMessagesRef = useRef(state.messages);

  // Keep refs in sync with state
  useEffect(() => {
    clientRef.current = state.client;
  }, [state.client]);

  useEffect(() => {
    activeConvoRef.current = state.activeConversationId;
  }, [state.activeConversationId]);

  useEffect(() => {
    stateConvosRef.current = state.conversations;
  }, [state.conversations]);

  useEffect(() => {
    stateMessagesRef.current = state.messages;
  }, [state.messages]);

  const chatAddress = getChatAddress({
    loginOption,
    activePublicKey: entityInfo.activePublicKey,
    email,
    indexWallet: entityInfo.indexWallet,
    hasPassword,
    userWalletsData: userWalletsData as any,
  });
  const chatAddressRef = useRef(chatAddress);
  useEffect(() => { chatAddressRef.current = chatAddress; }, [chatAddress]);

  // ---- Conversation cache helper: avoids redundant conversations.list() calls ----
  const getConversation = useCallback(async (conversationId: string): Promise<any | null> => {
    const client = clientRef.current;
    if (!client) return null;

    const cached = convoMapCacheRef.current.get(conversationId);
    if (cached) return cached;

    // Cache miss — refresh the list
    const allConvos = await client.conversations.list(LIST_ALL_CONSENT);
    convoMapCacheRef.current = new Map(allConvos.map((c: any) => [c.id || c.topic, c]));
    return convoMapCacheRef.current.get(conversationId) || null;
  }, []);

  // ---- Resolve HNS display names for DM peer addresses ----
  const resolveHnsDisplayNames = useCallback(async (conversations: ConversationData[]) => {
    const peerAddresses = conversations
      .filter((c) => !c.isGroup && c.peerAddress)
      .map((c) => c.peerAddress.toLowerCase());
    const unique = Array.from(new Set(peerAddresses));
    if (unique.length === 0) return;

    try {
      const data = await reverseHnsLookup(unique);
      const usernames: Record<string, string> = {};
      const displayNames: Record<string, string> = {};
      for (const [addr, entry] of Object.entries(data?.names || {})) {
        const e = entry as { username: string; display_name?: string };
        if (e?.username) {
          const key = addr.toLowerCase();
          usernames[key] = `@${e.username}`;
          if (e.display_name) displayNames[key] = e.display_name;
        }
      }
      if (Object.keys(usernames).length > 0) {
        dispatch({ type: 'SET_PEER_DISPLAY_NAMES', payload: { usernames, displayNames } });
      }
    } catch {
      // Non-critical — display names fall back to shortened addresses
    }
  }, []);

  // ---- Listen for tab takeover requests from other tabs ----
  // Always active so takeover requests are answered even during auto-init
  // or manual initialization (not just when fully initialized).
  useEffect(() => {
    const cleanup = listenForTakeover(() => {
      // Release the lock first — this MUST run before any cleanup that
      // could throw, so the requesting tab can always acquire the lock.
      releaseTabLock();

      // Persist current messages/conversations to cache before wiping state,
      // so the new tab can merge them back via loadMessages/syncConversations.
      try {
        const addr = chatAddress || chatAddressRef.current;
        if (addr && stateConvosRef.current?.length) {
          setCachedConversations(addr, stateConvosRef.current);
        }
        const msgs = stateMessagesRef.current || {};
        for (const [convoId, convoMsgs] of Object.entries(msgs)) {
          if (convoMsgs.length > 0) setCachedMessages(convoId, convoMsgs);
        }
      } catch { /* cache persistence is best-effort */ }

      try {
        const client = clientRef.current;
        if (client) closeXmtpClient(client);
        streamRef.current?.return?.();
        convoStreamRef.current?.return?.();
      } catch { /* cleanup errors are non-critical */ }
      clientRef.current = null;
      autoInitAttemptedRef.current = false;
      convoMapCacheRef.current = new Map();
      dispatch({ type: 'RESET' });
      dispatch({ type: 'SET_INIT_ERROR', payload: 'Chat was opened in another tab' });
    });

    return cleanup;
  }, []);

  // ---- Initialize XMTP ----
  const initializeChat = useCallback(
    async (password?: string) => {
      try {
        if (state.isInitialized || state.isInitializing) return;
        setIsLoading(true);
        // Tab lock check — request takeover if another tab holds the lock
        if (!acquireTabLock()) {
          dispatch({ type: 'SET_INIT_PROGRESS', payload: 'Taking over from another tab...' });
          dispatch({ type: 'SET_INITIALIZING', payload: true });
          await requestTabTakeover();
          // Retry with back-off — the other tab's BroadcastChannel listener may
          // need a moment to process the takeover and clear its lock interval,
          // especially if its main thread was busy with XMTP/OPFS operations.
          let acquired = acquireTabLock();
          for (let retry = 0; !acquired && retry < 3; retry++) {
            await new Promise(r => setTimeout(r, 300 * (retry + 1)));
            acquired = acquireTabLock();
          }
          if (!acquired) {
            dispatch({ type: 'SET_INITIALIZING', payload: false });
            toast.error('Could not take over chat from the other tab.');
            dispatch({ type: 'SET_INIT_ERROR', payload: 'Could not take over chat session' });
            return;
          }
        }

        dispatch({ type: 'SET_INITIALIZING', payload: true });
        dispatch({ type: 'SET_INIT_ERROR', payload: null });
        dispatch({ type: 'SET_INIT_PROGRESS', payload: 'Preparing chat...' });


        const authInfo = {
          loginOption,
          activePublicKey: entityInfo.activePublicKey,
          email,
          indexWallet: entityInfo.indexWallet,
          hasPassword,
          userWalletsData: userWalletsData as any,
        };

        // Close any existing client before creating a new one
        if (clientRef.current) {
          clientRef.current.close();
        }

        dispatch({ type: 'SET_INIT_PROGRESS', payload: 'Setting up wallet signer...' });
        const signer = createSigner(authInfo, password);
        pendingSignerRef.current = signer;
        setStoredSigner(signer);
        const address = chatAddress || '';
        const onProgress = (msg: string) => dispatch({ type: 'SET_INIT_PROGRESS', payload: msg });
        const { client, isNewRegistration } = await initializeXmtpClient(signer, address, onProgress);

        // If another tab took over while we were initializing, bail out
        if (!(window as any).__xmtpTabLockInterval) {
          try { client.close(); } catch { /* ignore */ }
          return;
        }

        isNewRegistrationRef.current = isNewRegistration;

        // Keep signer in ref for periodic background backups
        dispatch({ type: 'SET_INIT_PROGRESS', payload: 'Loading conversations...' });
        dispatch({ type: 'SET_CLIENT', payload: client });
        dispatch({ type: 'SET_INITIALIZED', payload: true });
        dispatch({ type: 'SET_INITIALIZING', payload: false });
        dispatch({ type: 'SET_HAS_CLOUD_BACKUP', payload: false });
        // Mark this device as successfully initialized so future page loads
        // skip the "Restore Chat" prompt and show "Enable Chat" instead.
        if (chatAddress) {
          localStorage.setItem('horus-xmtp-session-ok-' + chatAddress.toLowerCase(), '1');
        }
        setIsLoading(false);
      } catch (err: any) {
        // If we lost the lock mid-init (takeover), silently bail out
        if (!(window as any).__xmtpTabLockInterval) return;

        if (err?.name === 'InstallationLimitError' && err.inboxId) {
          dispatch({ type: 'SET_INSTALLATION_LIMIT', payload: { client: err.client || null, inboxId: err.inboxId } });
          return;
        }
        pendingSignerRef.current = null;
        setStoredSigner(null);
        console.error('Failed to initialize XMTP:', err);
        releaseTabLock();
        const errText = [err?.message, err?.shortMessage, err?.details].filter(Boolean).join(' ');
        const isRejection =
          /user rejected|user denied|rejected the request/i.test(errText);
        const friendlyMsg = isRejection
          ? 'User rejected the request.'
          : (err?.shortMessage || err?.message || 'Failed to initialize chat').replace(/\n[\s\S]*Version:[\s\S]*$/, '');
        dispatch({
          type: 'SET_INIT_ERROR',
          payload: friendlyMsg,
        });
        if (!isRejection) {
          toast.error('Failed to initialize chat. Please try again.');
        }
        setIsLoading(false);
      }
      finally {
        setIsLoading(false);
      }
    },
    [state.isInitialized, state.isInitializing, loginOption, entityInfo, email, hasPassword, userWalletsData, chatAddress]
  );

  // ---- Complete registration after user revokes installations ----
  const completeRegistration = useCallback(async () => {
    dispatch({ type: 'CLEAR_INSTALLATION_LIMIT' });
    dispatch({ type: 'SET_INITIALIZING', payload: true });
    dispatch({ type: 'SET_INIT_ERROR', payload: null });
    const onProgress = (msg: string) => dispatch({ type: 'SET_INIT_PROGRESS', payload: msg });

    try {
      let client: Client;
      const signer = pendingSignerRef.current;
      const address = chatAddress || '';
      if (state.pendingClient) {
        // We have a client (from register() failure) — just retry registration
        client = await registerAfterRevocation(state.pendingClient, onProgress, signer || undefined, address);
      } else {
        // No client (Client.create() failed) — full re-init with signer
        if (!signer) {
          throw new Error('No signer available. Please try enabling chat again.');
        }
        client = await createXmtpClient(signer, address, onProgress);
      }
      // If another tab took over while we were registering, bail out
      if (!(window as any).__xmtpTabLockInterval) {
        try { client.close(); } catch { /* ignore */ }
        return;
      }

      // Keep signer in ref for periodic background backups
      dispatch({ type: 'SET_INIT_PROGRESS', payload: 'Loading conversations...' });
      dispatch({ type: 'SET_CLIENT', payload: client });
      dispatch({ type: 'SET_INITIALIZED', payload: true });
      dispatch({ type: 'SET_INITIALIZING', payload: false });
      toast.success('Device registered successfully.', { duration: 5000 });
    } catch (err: any) {
      // If we lost the lock mid-registration (takeover), silently bail out
      if (!(window as any).__xmtpTabLockInterval) return;

      console.error('Registration after revocation failed:', err);
      releaseTabLock();
      dispatch({
        type: 'SET_INIT_ERROR',
        payload: (err?.shortMessage || err?.message || 'Failed to register after revoking installations').replace(/\n[\s\S]*Version:[\s\S]*$/, ''),
      });
      toast.error('Registration failed. Please try again.');
    }
  }, [state.pendingClient, chatAddress]);

  // ---- Dismiss installation manager without revoking ----
  const dismissInstallationManager = useCallback(() => {
    if (state.pendingClient) {
      try { state.pendingClient.close(); } catch { /* ignore */ }
    }
    pendingSignerRef.current = null;
    setStoredSigner(null);
    releaseTabLock();
    dispatch({ type: 'CLEAR_INSTALLATION_LIMIT' });
  }, [state.pendingClient]);

  // ---- Revoke installations (uses static Client method with stored signer) ----
  const revokeInstallations = useCallback(
    async (installationBytes: Uint8Array[], revokeAll: boolean) => {
      const client = state.pendingClient;
      const inboxId = state.pendingInboxId || client?.inboxId;
      const signer = pendingSignerRef.current;

      // Try instance method first (works if client was created with a signer)
      if (client) {
        try {
          if (revokeAll) {
            await client.revokeAllOtherInstallations();
          } else {
            await client.revokeInstallations(installationBytes);
          }
          return;
        } catch (instanceErr: any) {
          console.warn('Instance revocation failed, trying static method:', instanceErr.message);
        }
      }

      // Fallback: use static Client.revokeInstallations() with the stored signer
      if (!signer || !inboxId) {
        throw new Error('No signer or inbox ID available for revocation');
      }
      await (Client as any).revokeInstallations(
        signer,
        inboxId,
        installationBytes,
        XMTP_ENV,
      );
    },
    [state.pendingClient, state.pendingInboxId]
  );

  // ---- Force revoke ALL installations and reset (nuclear option, bypasses modal) ----
  const forceRevokeAllAndReset = useCallback(async (password?: string) => {
    if (!chatAddress) return;
    if (hasPassword && !password) {
      toast.error('Password is required for this operation.');
      return;
    }
    dispatch({ type: 'SET_INITIALIZING', payload: true });
    dispatch({ type: 'SET_INIT_PROGRESS', payload: 'Revoking all installations...' });

    try {
      // Step 1: Create signer for revocation
      const authInfo = {
        loginOption,
        activePublicKey: entityInfo.activePublicKey,
        email: email || '',
        indexWallet: entityInfo.indexWallet,
        hasPassword,
        userWalletsData: userWalletsData as any,
      };
      const signer = createSigner(authInfo, password);
      pendingSignerRef.current = signer;
      setStoredSigner(signer);

      // Step 2: Capture inboxId before closing the client
      const inboxId = state.client?.inboxId || '';

      // Step 3: Close existing client and clear local state
      await forceResetXmtpClient(state.client, chatAddress);
      localStorage.removeItem('horus-xmtp-session-ok-' + chatAddress.toLowerCase());
      dispatch({ type: 'RESET' });
      dispatch({ type: 'SET_INITIALIZING', payload: true });
      dispatch({ type: 'SET_INIT_PROGRESS', payload: 'Revoking all installations...' });
      let installationIds: Uint8Array[] = [];
      if (inboxId) {
        try {
          const states = await (Client as any).fetchInboxStates([inboxId], XMTP_ENV);
          const inboxState = states?.[0];
          if (inboxState?.installations) {
            installationIds = inboxState.installations.map((inst: any) => inst.bytes);
          }
        } catch {
          // If we can't fetch, proceed anyway — createXmtpClient will handle it
        }
      }

      // Step 4: Revoke all installations via static API
      if (installationIds.length > 0 && inboxId) {
        dispatch({ type: 'SET_INIT_PROGRESS', payload: `Revoking ${installationIds.length} installations...` });
        try {
          await (Client as any).revokeInstallations(signer, inboxId, installationIds, XMTP_ENV);
          // Wait for network propagation
          await new Promise(r => setTimeout(r, 3000));
        } catch (revErr: any) {
          console.warn('[XMTP] Revocation error (proceeding anyway):', revErr.message);
        }
      }

      // Step 5: Create fresh client with new installation
      dispatch({ type: 'SET_INIT_PROGRESS', payload: 'Creating new chat identity...' });
      const client = await createXmtpClient(signer, chatAddress, (msg) => {
        dispatch({ type: 'SET_INIT_PROGRESS', payload: msg });
      });

      dispatch({ type: 'SET_CLIENT', payload: client });
      dispatch({ type: 'SET_INITIALIZED', payload: true });
      dispatch({ type: 'SET_INITIALIZING', payload: false });
      toast.success('All installations revoked and chat identity refreshed!');
    } catch (err: any) {
      console.error('[XMTP Force Reset] Failed:', err);
      const cleanMsg = (err?.shortMessage || err?.message || 'Force reset failed').replace(/\n[\s\S]*Version:[\s\S]*$/, '');
      dispatch({ type: 'SET_INIT_ERROR', payload: cleanMsg });
      toast.error('Force reset failed: ' + cleanMsg);
    }
  }, [chatAddress, state.client, loginOption, entityInfo, email, hasPassword, userWalletsData]);

  // ---- Auto-initialize: try cached DB first, then full init (no user click needed) ----
  useEffect(() => {
    if (state.isInitialized || !chatAddress || !isLoggedIn) return;
    if (autoInitAttemptedRef.current) return;
    autoInitAttemptedRef.current = true;

    async function tryAutoInit() {
      if (!acquireTabLock()) {
        // Another tab is active — silently back off, don't auto-takeover
        return;
      }

      dispatch({ type: 'SET_INITIALIZING', payload: true });
      dispatch({ type: 'SET_INIT_ERROR', payload: null });
      const onProgress = (msg: string) => dispatch({ type: 'SET_INIT_PROGRESS', payload: msg });

      try {
        // Step 1: Try to rebuild from cached OPFS database (no signing needed).
        // buildXmtpClient retries internally (5 attempts with backoff) for OPFS
        // lock contention and preserves data on lock errors.
        onProgress('Reconnecting from cached session...');
        let cachedClient = await buildXmtpClient(chatAddress!, onProgress);

        // Step 1b: If build failed and we have no local DB key, try recovering
        // it from Firebase (no signer needed, just cached Firebase session).
        // This handles the case where localStorage was cleared but OPFS + Firebase
        // still have the data.
        if (!cachedClient) {
          try {
            const silentAuth = await trySilentFirebaseAuth();
            if (silentAuth) {
              onProgress('Recovering encryption key...');
              const recoveredKey = await ensureDbKeyFromFirebase(chatAddress!);
              if (recoveredKey) {
                cachedClient = await buildXmtpClient(chatAddress!, onProgress);
              }
            }
          } catch {
            // Silent recovery is non-critical
          }
        }

        if (cachedClient) {
          let registered = false;
          try {
            registered = await cachedClient.isRegistered();
          } catch (regErr) {
            // Network/transient error — local OPFS data is valid, use it anyway.
            console.warn('isRegistered() failed during auto-init (transient), using local client:', regErr);
            registered = true;
          }

          if (registered) {
            // If another tab took over while we were checking, bail out
            if (!(window as any).__xmtpTabLockInterval) {
              try { cachedClient.close(); } catch { /* ignore */ }
              return;
            }

            // Create signer for background Firebase sync (if possible without user interaction)
            if (!hasPassword) {
              try {
                const authInfo = {
                  loginOption,
                  activePublicKey: entityInfo.activePublicKey,
                  email,
                  indexWallet: entityInfo.indexWallet,
                  hasPassword,
                  userWalletsData: userWalletsData as any,
                };
                const signer = createSigner(authInfo);
                pendingSignerRef.current = signer;
                setStoredSigner(signer);

                (async () => {
                  try {
                    await ensureFirebaseBackup(chatAddress!, signer);
                  } catch {
                    // Firebase backup is non-critical
                  }
                })();
              } catch {
                // Signer creation for backup is non-critical
              }
            }
            onProgress('Loading conversations...');
            dispatch({ type: 'SET_CLIENT', payload: cachedClient });
            dispatch({ type: 'SET_INITIALIZED', payload: true });
            dispatch({ type: 'SET_INITIALIZING', payload: false });
            dispatch({ type: 'SET_HAS_CLOUD_BACKUP', payload: false });
            if (chatAddress) {
              localStorage.setItem('horus-xmtp-session-ok-' + chatAddress.toLowerCase(), '1');
            }
            return;
          }
          cachedClient.close();
        }

        // Step 2: Cache unavailable — try full init with signer
        // For password users: we can't create a signer without the password,
        // so keep the tab lock (prevent other tabs from starting) and show prompt.
        if (hasPassword) {
          // Don't release tab lock — hold it so user can proceed without takeover dance.
          dispatch({ type: 'SET_INITIALIZING', payload: false });
          dispatch({ type: 'SET_INIT_PROGRESS', payload: null });

          // Use trySilentFirebaseAuth (no popups) to check backup existence
          const sessionFlag = localStorage.getItem('horus-xmtp-session-ok-' + chatAddress!.toLowerCase());
          if (!sessionFlag) {
            (async () => {
              try {
                const silentAuth = await trySilentFirebaseAuth();
                if (silentAuth) {
                  const exists = await hasFirebaseBackup(chatAddress!);
                  dispatch({ type: 'SET_HAS_CLOUD_BACKUP', payload: exists });
                } else {
                  dispatch({ type: 'SET_HAS_CLOUD_BACKUP', payload: null });
                }
              } catch {
                dispatch({ type: 'SET_HAS_CLOUD_BACKUP', payload: null });
              }
            })();
          }
          return;
        }

        // Non-password users: create signer automatically
        const authInfo = {
          loginOption,
          activePublicKey: entityInfo.activePublicKey,
          email,
          indexWallet: entityInfo.indexWallet,
          hasPassword,
          userWalletsData: userWalletsData as any,
        };

        onProgress('Setting up wallet signer...');
        const signer = createSigner(authInfo);
        pendingSignerRef.current = signer;
        setStoredSigner(signer);
        const { client, isNewRegistration } = await initializeXmtpClient(
          signer,
          chatAddress!,
          onProgress,
          { allowCreateNew: false }
        );

        // If another tab took over while we were initializing, bail out
        if (!(window as any).__xmtpTabLockInterval) {
          try { client.close(); } catch { /* ignore */ }
          return;
        }

        isNewRegistrationRef.current = isNewRegistration;
        onProgress('Loading conversations...');
        dispatch({ type: 'SET_CLIENT', payload: client });
        dispatch({ type: 'SET_INITIALIZED', payload: true });
        dispatch({ type: 'SET_INITIALIZING', payload: false });
        dispatch({ type: 'SET_HAS_CLOUD_BACKUP', payload: false });
        if (chatAddress) {
          localStorage.setItem('horus-xmtp-session-ok-' + chatAddress.toLowerCase(), '1');
        }
      } catch (err: any) {
        // If we lost the lock mid-init (takeover), silently bail out
        if (!(window as any).__xmtpTabLockInterval) return;

        if (err?.name === 'InstallationLimitError' && err.inboxId) {
          dispatch({ type: 'SET_INSTALLATION_LIMIT', payload: { client: err.client || null, inboxId: err.inboxId } });
          return;
        }
        pendingSignerRef.current = null;
        setStoredSigner(null);
        releaseTabLock();

        // For non-password users whose allowCreateNew=false failed silently,
        // show the Enable Chat button so they can trigger full init manually.
        dispatch({ type: 'SET_INITIALIZING', payload: false });
        dispatch({ type: 'SET_INIT_PROGRESS', payload: null });
        dispatch({ type: 'SET_HAS_CLOUD_BACKUP', payload: null });
      }
    }

    tryAutoInit();
  }, [chatAddress, isLoggedIn, state.isInitialized, loginOption, entityInfo, email, hasPassword, userWalletsData]);

  // ---- Reset chat (force-clear OPFS and reinitialize) ----
  const resetChat = useCallback(async (password?: string) => {
    if (!chatAddress) return;
    if (hasPassword && !password) {
      toast.error('Password is required for this operation.');
      return;
    }
    dispatch({ type: 'SET_INITIALIZING', payload: true });
    dispatch({ type: 'SET_INIT_PROGRESS', payload: 'Resetting chat identity...' });
    try {
      // Close existing client and clear all local state
      await forceResetXmtpClient(state.client, chatAddress);
      localStorage.removeItem('horus-xmtp-session-ok-' + chatAddress.toLowerCase());
      dispatch({ type: 'RESET' });

      // Reinitialize with fresh identity
      dispatch({ type: 'SET_INITIALIZING', payload: true });
      dispatch({ type: 'SET_INIT_PROGRESS', payload: 'Creating new chat identity...' });

      const authInfo = {
        loginOption,
        activePublicKey: entityInfo.activePublicKey,
        email: email || '',
        indexWallet: entityInfo.indexWallet,
        hasPassword,
        userWalletsData: userWalletsData as any,
      };
      const signer = createSigner(authInfo, password);
      pendingSignerRef.current = signer;
      setStoredSigner(signer);

      const client = await createXmtpClient(signer, chatAddress, (msg) => {
        dispatch({ type: 'SET_INIT_PROGRESS', payload: msg });
      });

      dispatch({ type: 'SET_CLIENT', payload: client });
      dispatch({ type: 'SET_INITIALIZED', payload: true });
      dispatch({ type: 'SET_INITIALIZING', payload: false });
      toast.success('Chat identity refreshed successfully');
    } catch (err: any) {
      if (err?.name === 'InstallationLimitError' && err.inboxId) {
        // Too many installations registered — show the revocation modal
        dispatch({ type: 'SET_INSTALLATION_LIMIT', payload: { client: err.client || null, inboxId: err.inboxId } });
        return;
      }
      console.error('[XMTP Reset] Failed:', err);
      dispatch({ type: 'SET_INIT_ERROR', payload: err?.message || 'Reset failed' });
      toast.error('Failed to reset chat. Please try again.');
    }
  }, [chatAddress, state.client, loginOption, entityInfo, email, hasPassword, userWalletsData]);

  // Keep resetChatRef in sync so sendMessage can trigger it from toast
  useEffect(() => {
    resetChatRef.current = resetChat;
  }, [resetChat]);

  // ---- Sync conversations (two-phase: fast sidebar, then background enrichment) ----
  const syncConversations = useCallback(async () => {
    const client = clientRef.current;
    if (!client) return;

    dispatch({ type: 'SET_SYNCING', payload: true });
    try {
      // Two-phase sync:
      // 1. conversations.sync() — fetches new welcome messages and conversations from network
      // 2. conversations.syncAll() — processes welcomes and syncs messages for all conversations
      // Note: syncAll() called WITHOUT consent filtering to process ALL welcome messages.
      // Step 1: conversations.sync() — fetch welcome messages (lightweight)
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          await client.conversations.sync();
          break;
        } catch {
          if (attempt < 3) await new Promise((r) => setTimeout(r, 2000));
        }
      }

      // Step 2: syncAll() — sync messages for all conversations
      // May fail on corrupted groups but should not prevent conversation listing.
      // Retry once on failure since transient network issues are common.
      let syncAllOk = false;
      for (let syncAttempt = 1; syncAttempt <= 2; syncAttempt++) {
        try {
          await Promise.race([
            client.conversations.syncAll(SYNC_ALL_CONSENT),
            new Promise((_, reject) => setTimeout(() => reject(new Error('syncAll timeout')), 30000)),
          ]);
          syncAllOk = true;
          break;
        } catch (syncErr: any) {
          console.warn(`[XMTP Sync] syncAll attempt ${syncAttempt}/2 failed:`, syncErr?.message);
          if (syncAttempt < 2) await new Promise((r) => setTimeout(r, 2000));
        }
      }
      if (!syncAllOk) {
        // Fallback: sync only the active conversation so its messages are current
        const activeId = activeConvoRef.current;
        if (activeId) {
          const activeConvo = convoMapCacheRef.current.get(activeId);
          if (activeConvo) {
            try { await activeConvo.sync(); } catch { /* non-critical */ }
          }
        }
      }

      const allConvos = await client.conversations.list(LIST_ALL_CONSENT);

      // Populate conversation object cache
      convoMapCacheRef.current = new Map(allConvos.map((c: any) => [c.id || c.topic, c]));

      // Phase 1: Build lightweight conversation metadata — no per-convo sync or message loading
      const conversations: ConversationData[] = await Promise.all(
        allConvos.map((convo: any) => toConversationLight(convo, client))
      );

      // Merge with cached previews so existing preview data shows immediately
      if (chatAddress) {
        const cachedConvos = getCachedConversations(chatAddress);
        if (cachedConvos) {
          const cachedMap = new Map(cachedConvos.map((c) => [c.id, c]));
          conversations.forEach((convo) => {
            const cached = cachedMap.get(convo.id);
            if (!cached) return;
            if (cached.lastMessageText) {
              convo.lastMessageText = cached.lastMessageText;
              convo.lastMessageTime = cached.lastMessageTime;
              convo.lastMessageSender = cached.lastMessageSender;
            }
          });
        }
      }

      // Sort and dispatch immediately — sidebar renders now
      conversations.sort((a, b) => {
        const timeA = a.lastMessageTime?.getTime() || a.createdAt.getTime();
        const timeB = b.lastMessageTime?.getTime() || b.createdAt.getTime();
        return timeB - timeA;
      });

      dispatch({ type: 'SET_CONVERSATIONS', payload: conversations });

      // Resolve HNS display names for DM peers (non-blocking)
      resolveHnsDisplayNames(conversations);

      // Phase 2: Enrich with message previews and unread counts in background batches
      // SET_SYNCING stays true until Phase 2 completes to prevent visual jumps
      const myInboxId = client.inboxId || '';
      for (let i = 0; i < allConvos.length; i += ENRICH_BATCH_SIZE) {
        const batch = allConvos.slice(i, i + ENRICH_BATCH_SIZE);

        await Promise.all(
          batch.map(async (convo: any, batchIdx: number) => {
            const idx = i + batchIdx;
            const convoData = conversations[idx];
            if (!convoData) return;

            try {
              await convo.sync().catch(() => { });
              const recentMsgs = await convo.messages({ limit: BigInt(5), direction: SortDirection.Ascending });
              const lastReadTime = getLastReadTime(convoData.id);

              const parsedMsgs = recentMsgs
                .map((m: any) => toMessage(m, myInboxId))
                .filter((m: any): m is ChatMessage => m !== null);

              if (parsedMsgs.length > 0) {
                const latest = parsedMsgs[parsedMsgs.length - 1];
                const textPreview =
                  typeof latest.content === 'string'
                    ? latest.content.slice(0, 100)
                    : latest.content?.isVoiceNote
                      ? '\uD83C\uDFA4 Voice message'
                      : '[Attachment]';

                // Only update if network data is newer than cached preview
                const cachedTime = convoData.lastMessageTime?.getTime() || 0;
                if (latest.sentAt.getTime() >= cachedTime) {
                  dispatch({
                    type: 'UPDATE_CONVERSATION_LAST_MESSAGE',
                    payload: { id: convoData.id, text: textPreview, time: latest.sentAt, sender: latest.senderAddress },
                  });
                }
              }

              // Count unread
              let unread = 0;
              const userSentAfterLastRead = parsedMsgs.some(
                (m: ChatMessage) => m.isSentByMe && m.sentAt.getTime() > lastReadTime
              );
              if (!userSentAfterLastRead) {
                for (const msg of parsedMsgs) {
                  if (!msg.isSentByMe && msg.sentAt.getTime() > lastReadTime) unread++;
                }
              }
              if (unread > 0) {
                dispatch({ type: 'SET_UNREAD_COUNT', payload: { conversationId: convoData.id, count: unread } });
              }
            } catch {
              // Not critical — conversation still shows
            }
          })
        );
      }

      // Persist enriched conversations to localStorage
      if (chatAddress) {
        setCachedConversations(chatAddress, conversations);
      }

      dispatch({ type: 'SET_SYNCING', payload: false });
    } catch (err) {
      console.error('Error syncing conversations:', err);
      dispatch({ type: 'SET_SYNCING', payload: false });
    }
  }, [chatAddress]);

  // ---- Load messages for a conversation ----
  const loadMessages = useCallback(async (conversationId: string) => {
    const client = clientRef.current;
    if (!client) return;

    // Load cached messages instantly while network sync happens
    const cached = getCachedMessages(conversationId);
    if (cached && cached.length > 0) {
      dispatch({ type: 'SET_MESSAGES', payload: { conversationId, messages: cached } });
    }

    dispatch({ type: 'SET_LOADING_MESSAGES', payload: { conversationId, loading: true } });

    try {
      const convo = await getConversation(conversationId);
      if (!convo) return;

      await convo.sync().catch(() => { });
      const msgs = await convo.messages({ limit: BigInt(MESSAGE_PAGE_SIZE), direction: SortDirection.Ascending });

      const myInboxId = client.inboxId || '';
      const networkMessages: ChatMessage[] = msgs
        .map((m: any) => toMessage(m, myInboxId))
        .filter((m: ChatMessage | null): m is ChatMessage => m !== null);

      // Resolve sender inbox IDs to Ethereum addresses for display
      const uniqueSenderIds = Array.from(new Set(networkMessages.map((m) => m.senderAddress)));
      const addressMap = new Map<string, string>();
      await Promise.all(
        uniqueSenderIds.map(async (inboxId) => {
          const addr = await resolveInboxIdToAddress(client, inboxId);
          addressMap.set(inboxId, addr);
        })
      );
      networkMessages.forEach((m) => {
        m.senderAddress = addressMap.get(m.senderAddress) || m.senderAddress;
      });

      // Merge network messages with cached messages so that any
      // stream-delivered messages in the cache aren't lost if the
      // network sync hasn't caught up yet.
      // Filter out local- placeholder messages from cache (they would
      // duplicate real messages from the network that have different IDs).
      let merged = cached
        ? mergeMessages(
          cached.filter((m) => !m.id.startsWith('local-')),
          networkMessages
        )
        : networkMessages;

      // Merge archived messages that XMTP may have lost (older messages)
      if (chatAddress) {
        try {
          const archivedMsgs = await restoreConversationMessages(chatAddress, conversationId);
          if (archivedMsgs.length > 0) {
            merged = mergeMessages(archivedMsgs, merged);
          }
        } catch { /* archive merge is non-critical */ }
      }

      dispatch({ type: 'SET_MESSAGES', payload: { conversationId, messages: merged } });
      dispatch({
        type: 'SET_HAS_MORE_MESSAGES',
        payload: { conversationId, hasMore: msgs.length >= MESSAGE_PAGE_SIZE },
      });

      // Persist merged messages to localStorage
      setCachedMessages(conversationId, merged);

      // Update sidebar preview with the most recent message
      if (merged.length > 0) {
        const latest = merged[merged.length - 1];
        let textPreview: string;
        if (latest.contentType === 'remoteAttachment' && latest.content?.isVoiceNote) {
          textPreview = '\uD83C\uDFA4 Voice message';
        } else if (typeof latest.content === 'string') {
          textPreview = latest.content.slice(0, 100);
        } else {
          textPreview = latest.contentType === 'remoteAttachment' ? '[Attachment]' : '[Attachment]';
        }
        dispatch({
          type: 'UPDATE_CONVERSATION_LAST_MESSAGE',
          payload: {
            id: conversationId,
            text: textPreview,
            time: latest.sentAt,
            sender: latest.senderAddress,
          },
        });
      }
    } catch (err) {
      console.error('Error loading messages:', err);
    } finally {
      dispatch({ type: 'SET_LOADING_MESSAGES', payload: { conversationId, loading: false } });
    }
  }, []);

  // ---- Load older messages (pagination) ----
  const loadOlderMessages = useCallback(
    async (conversationId: string) => {
      const client = clientRef.current;
      if (!client) return;

      const existingMessages = state.messages[conversationId] || [];
      if (existingMessages.length === 0) return;

      dispatch({ type: 'SET_LOADING_MESSAGES', payload: { conversationId, loading: true } });

      try {
        const convo = await getConversation(conversationId);
        if (!convo) return;

        // Use the oldest message's timestamp as cursor to fetch earlier messages
        const oldestMessage = existingMessages[0];
        const beforeNs = BigInt(oldestMessage.sentAt.getTime()) * BigInt(1_000_000);

        const msgs = await convo.messages({
          limit: BigInt(MESSAGE_PAGE_SIZE),
          sentBeforeNs: beforeNs,
          direction: SortDirection.Ascending,
        });

        const myInboxId = client.inboxId || '';
        const existingIds = new Set(existingMessages.map((m) => m.id));
        const olderMsgs: ChatMessage[] = msgs
          .map((m: any) => toMessage(m, myInboxId))
          .filter((m: ChatMessage | null): m is ChatMessage => m !== null)
          .filter((m: ChatMessage) => !existingIds.has(m.id));

        // Resolve sender addresses
        const uniqueSenderIds = Array.from(new Set(olderMsgs.map((m: ChatMessage) => m.senderAddress)));
        const addressMap = new Map<string, string>();
        await Promise.all(
          uniqueSenderIds.map(async (inboxId) => {
            const addr = await resolveInboxIdToAddress(client, inboxId);
            addressMap.set(inboxId, addr);
          })
        );
        olderMsgs.forEach((m: ChatMessage) => {
          m.senderAddress = addressMap.get(m.senderAddress) || m.senderAddress;
        });

        if (olderMsgs.length > 0) {
          dispatch({ type: 'PREPEND_MESSAGES', payload: { conversationId, messages: olderMsgs } });
        }
        dispatch({
          type: 'SET_HAS_MORE_MESSAGES',
          payload: { conversationId, hasMore: msgs.length >= MESSAGE_PAGE_SIZE },
        });
      } catch (err) {
        console.error('Error loading older messages:', err);
      } finally {
        dispatch({ type: 'SET_LOADING_MESSAGES', payload: { conversationId, loading: false } });
      }
    },
    [state.messages]
  );

  // ---- Send message ----
  const sendMessage = useCallback(
    async (conversationId: string, text: string, attachment?: RemoteAttachmentMetadata) => {
      const client = clientRef.current;
      if (!client || (!text.trim() && !attachment)) return;

      try {
        let convo = await getConversation(conversationId);
        if (!convo) {
          toast.error('Conversation not found');
          return;
        }

        const messageContent = attachment
          ? ATTACHMENT_PREFIX + JSON.stringify(attachment)
          : text.trim();

        // Sync conversation state before sending to ensure MLS group is current
        try {
          await convo.sync();
        } catch {
          // Pre-send sync is non-blocking
        }

        // sendText() publishes the message to the network, then runs an internal
        // sync. If the MLS group has stale UpdateGroupMembership intents, the
        // post-send sync throws even though the message was already delivered.
        // Detect "0 failed N succeeded" to treat these as successful sends.
        let sendRecovered = false;
        try {
          await convo.sendText(messageContent);
        } catch (sendErr: any) {
          const errMsg = sendErr?.message || '';
          const syncMatch = errMsg.match(/(\d+)\s+failed\s+(\d+)\s+succeeded/);
          if (syncMatch && syncMatch[1] === '0' && parseInt(syncMatch[2]) > 0) {
            // Message was published — error is from post-send sync on stale intents
            console.warn('[XMTP] sendText post-sync error (message delivered):', errMsg);
            sendRecovered = true;
          } else if (errMsg.includes('inactive') || errMsg.includes('Sync failed')) {
            // "Group is inactive" — conversation object is stale.
            // Force-refresh via syncAll + re-list and retry once.
            console.warn('[XMTP] Group inactive — refreshing conversation and retrying...');
            try {
              await client.conversations.syncAll(SYNC_ALL_CONSENT);
              const freshConvos = await client.conversations.list(LIST_ALL_CONSENT);
              convoMapCacheRef.current = new Map(freshConvos.map((c: any) => [c.id || c.topic, c]));
              const freshConvo = convoMapCacheRef.current.get(conversationId);
              if (freshConvo) {
                convo = freshConvo;
                await convo.sync();
                await convo.sendText(messageContent);
                sendRecovered = true;
              } else {
                throw sendErr;
              }
            } catch (refreshErr: any) {
              console.error('[XMTP] Refresh retry also failed:', refreshErr?.message);
              throw sendErr;
            }
          } else {
            // Optimistic send fallback — stores locally then publishes
            // without triggering the sync that causes InboxValidationFailed
            try {
              await convo.sendText(messageContent, true);
              await convo.publishMessages();
              console.warn('[XMTP] Used optimistic send fallback. Original error:', errMsg);
              sendRecovered = true;
            } catch {
              throw sendErr;
            }
          }
        }

        // Optimistic local append: fetch the just-sent message from local DB
        // so it appears immediately without waiting for the stream echo.
        // Uses the real SDK message ID, so the stream dedup will catch the echo.
        const myInboxId = client.inboxId || '';
        try {
          // Default direction is descending (newest first), so index 0 = just-sent message
          const recentMsgs = await convo.messages({ limit: BigInt(1) });
          if (recentMsgs.length > 0) {
            const sentMessage = toMessage(recentMsgs[0], myInboxId);
            if (sentMessage && sentMessage.isSentByMe) {
              try {
                const resolved = await resolveInboxIdToAddress(client, sentMessage.senderAddress);
                if (resolved) sentMessage.senderAddress = resolved;
              } catch { /* non-critical */ }
              dispatch({
                type: 'APPEND_MESSAGE',
                payload: { conversationId, message: sentMessage },
              });
              const existingCache = getCachedMessages(conversationId) || [];
              if (!existingCache.some(m => m.id === sentMessage.id)) {
                existingCache.push(sentMessage);
                setCachedMessages(conversationId, existingCache);
              }
            }
          }
        } catch { /* non-critical — stream will deliver */ }

        // Update sidebar preview so conversation order stays current
        const sentTime = new Date();
        let textPreview: string;
        if (attachment?.isVoiceNote) {
          textPreview = '\uD83C\uDFA4 Voice message';
        } else if (attachment) {
          textPreview = '[Attachment]';
        } else {
          textPreview = text.trim().slice(0, 100);
        }
        dispatch({
          type: 'UPDATE_CONVERSATION_LAST_MESSAGE',
          payload: {
            id: conversationId,
            text: textPreview,
            time: sentTime,
            sender: myInboxId,
          },
        });

        if (chatAddress) {
          updateConversationCachePreview(chatAddress, conversationId, textPreview, sentTime, myInboxId);
        }

        setLastReadTime(conversationId);

        // Schedule a backup so the sent message is persisted to Firebase
        (window as any).__horusScheduleBackup?.();
        (window as any).__horusScheduleArchiveSync?.();
      } catch (err: any) {
        console.error('Error sending message:', err);

        // For group conversations, check if the failure is due to removal
        const convoData = (stateConvosRef.current || []).find(
          (c: ConversationData) => c.id === conversationId
        );
        if (convoData?.isGroup) {
          try {
            const rawConvo = convoMapCacheRef.current.get(conversationId);
            if (rawConvo) {
              try { await rawConvo.sync(); } catch { /* may fail if evicted */ }
              let rawMembers: any[] = [];
              try {
                rawMembers = typeof rawConvo.members === 'function'
                  ? await rawConvo.members()
                  : Array.isArray(rawConvo.members) ? rawConvo.members : [];
              } catch { /* evicted */ }

              const myAddr = chatAddressRef.current?.toLowerCase();
              if (myAddr) {
                const stillMember = rawMembers.length > 0 && (
                  await Promise.all(
                    rawMembers.map((m: any) => resolveInboxIdToAddress(client!, m.inboxId || m))
                  )
                ).some((a) => a.toLowerCase() === myAddr);

                if (!stillMember) {
                  dispatch({ type: 'MARK_REMOVED_FROM_GROUP', payload: conversationId });
                  toast.error('You have been removed from this group');
                  return;
                }
              }
            }
          } catch { /* membership check failed — fall through to generic error */ }
        }
        toast.error('Failed to send message');
      }
    },
    [chatAddress]
  );

  // ---- Select conversation ----
  const selectConversation = useCallback((conversationId: string | null) => {
    if (conversationId) {
      // Snapshot the last-read time BEFORE marking as read (for new-messages divider)
      lastReadAtRef.current = getLastReadTime(conversationId);
      dispatch({ type: 'CLEAR_UNREAD', payload: conversationId });
      setLastReadTime(conversationId);
    } else {
      lastReadAtRef.current = 0;
    }
    dispatch({ type: 'SET_ACTIVE_CONVERSATION', payload: conversationId });
  }, []);

  // ---- Create DM ----
  const createDm = useCallback(
    async (peerAddress: string): Promise<string | null> => {
      const client = clientRef.current;
      if (!client) return null;

      // If a DM with this peer already exists, navigate to it instead of creating a duplicate
      const existingConvo = stateConvosRef.current.find(
        (c) => !c.isGroup && c.peerAddress && c.peerAddress.toLowerCase() === peerAddress.toLowerCase()
      );
      if (existingConvo) {
        selectConversation(existingConvo.id);
        return existingConvo.id;
      }

      try {
        // Check if the address is reachable on XMTP before attempting DM
        const reachable = await canMessage(client, [peerAddress]);
        if (!reachable.get(peerAddress.toLowerCase())) {
          toast.error('This address is not registered on XMTP and cannot receive messages yet.');
          return null;
        }

        const identifier = {
          identifier: peerAddress.toLowerCase(),
          identifierKind: IdentifierKind.Ethereum,
        };
        const dm = await client.conversations.createDmWithIdentifier(identifier);

        // Cache the raw SDK conversation object so sendMessage can find it immediately
        const dmId = dm.id || (dm as any).topic;
        if (dmId) {
          convoMapCacheRef.current.set(dmId, dm);
        }

        // Sync only the new DM to publish the welcome message, NOT syncAll()
        // which would trigger UpdateGroupMembership on all groups (causing
        // InboxValidationFailed errors on groups with corrupted MLS state).
        try {
          await client.conversations.sync();
          await dm.sync();
        } catch {
          // Post-DM sync is non-critical
        }

        const convoData = { ...(await toConversation(dm, client)), peerAddress: peerAddress.toLowerCase() };
        dispatch({ type: 'ADD_CONVERSATION', payload: convoData });
        return convoData.id;
      } catch (err: any) {
        console.error('Error creating DM:', err);
        toast.error('Failed to start conversation');
        return null;
      }
    },
    []
  );

  // ---- Create Group ----
  const createGroup = useCallback(
    async (memberAddresses: string[], name?: string): Promise<string | null> => {
      const client = clientRef.current;
      if (!client) return null;

      try {
        // Filter out addresses not registered on XMTP
        const reachable = await canMessage(client, memberAddresses);
        const reachableAddrs = memberAddresses.filter(
          (a) => reachable.get(a.toLowerCase())
        );
        const skipped = memberAddresses.filter(
          (a) => !reachable.get(a.toLowerCase())
        );

        if (skipped.length > 0) {
          const short = skipped.map((a) => `${a.slice(0, 6)}…${a.slice(-4)}`);
          toast(
            `Skipped ${skipped.length} address(es) not on XMTP: ${short.join(', ')}`
          );
        }

        if (reachableAddrs.length === 0) {
          toast.error('None of the provided addresses are registered on XMTP.');
          return null;
        }

        const identifiers = reachableAddrs.map((addr) => ({
          identifier: addr.toLowerCase(),
          identifierKind: IdentifierKind.Ethereum,
        }));
        const group = await client.conversations.createGroupWithIdentifiers(
          identifiers,
          { groupName: name || undefined }
        );

        // MLS welcome messages are published automatically by newGroupWithIdentifiers().
        // Sync to update local state.
        try {
          await client.conversations.sync();
        } catch {
          // Post-group sync is non-critical
        }

        const convoData = await toConversation(group, client);
        convoData.isGroup = true;
        convoData.groupName = name;

        // Ensure members are populated immediately — toConversation may return
        // an empty list if MLS state hasn't settled yet after creation.
        if (!convoData.members || convoData.members.length === 0) {
          const myAddress = chatAddressRef.current?.toLowerCase() || '';
          const uniqueMembers = new Set<string>();
          if (myAddress) uniqueMembers.add(myAddress);
          reachableAddrs.forEach(a => uniqueMembers.add(a.toLowerCase()));
          convoData.members = Array.from(uniqueMembers);
        }

        dispatch({ type: 'ADD_CONVERSATION', payload: convoData });
        return convoData.id;
      } catch (err: any) {
        console.error('Error creating group:', err);
        toast.error('Failed to create group');
        return null;
      }
    },
    []
  );

  // ---- Add members to group ----
  const addGroupMembers = useCallback(
    async (conversationId: string, addresses: string[]) => {
      const client = clientRef.current;
      if (!client) return;

      // Filter out addresses not registered on XMTP
      const reachable = await canMessage(client, addresses);
      const reachableAddrs = addresses.filter(
        (a) => reachable.get(a.toLowerCase())
      );
      const skipped = addresses.filter(
        (a) => !reachable.get(a.toLowerCase())
      );

      if (skipped.length > 0) {
        const short = skipped.map((a) => `${a.slice(0, 6)}…${a.slice(-4)}`);
        toast(
          `Skipped ${skipped.length} address(es) not on XMTP: ${short.join(', ')}`
        );
      }

      if (reachableAddrs.length === 0) {
        toast.error('None of the provided addresses are registered on XMTP.');
        return;
      }

      // Optimistic update: immediately reflect new members in the UI
      const currentConvo = stateConvosRef.current.find(c => c.id === conversationId);
      const existingMembers = currentConvo?.members || [];
      const newAddrs = reachableAddrs
        .map(a => a.toLowerCase())
        .filter(a => !existingMembers.some(m => m.toLowerCase() === a));
      if (newAddrs.length > 0) {
        dispatch({ type: 'UPDATE_CONVERSATION_MEMBERS', payload: { id: conversationId, members: [...existingMembers, ...newAddrs] } });
      }

      try {
        const convo = await getConversation(conversationId);
        if (!convo) throw new Error('Conversation not found');

        await convo.sync();
        const identifiers = reachableAddrs.map((addr) => ({
          identifier: addr.toLowerCase(),
          identifierKind: IdentifierKind.Ethereum,
        }));
        await (convo as any).addMembersByIdentifiers(identifiers);
        await convo.sync();

        // Reconcile with authoritative member list from SDK
        const updated = await toConversation(convo, client);
        dispatch({ type: 'UPDATE_CONVERSATION_MEMBERS', payload: { id: conversationId, members: updated.members || [] } });
        toast.success(`Added ${reachableAddrs.length} member(s)`);
      } catch (err: any) {
        // Revert optimistic update on failure
        dispatch({ type: 'UPDATE_CONVERSATION_MEMBERS', payload: { id: conversationId, members: existingMembers } });
        console.error('Error adding group members:', err);
        toast.error(err.message || 'Failed to add members');
      }
    },
    []
  );

  // ---- Remove member from group ----
  const removeGroupMember = useCallback(
    async (conversationId: string, address: string) => {
      const client = clientRef.current;
      if (!client) return;

      try {
        const convo = await getConversation(conversationId);
        if (!convo) throw new Error('Conversation not found');

        await convo.sync();
        const identifier = {
          identifier: address.toLowerCase(),
          identifierKind: IdentifierKind.Ethereum,
        };
        await (convo as any).removeMembersByIdentifiers([identifier]);
        await convo.sync();

        // Refresh member list
        const updated = await toConversation(convo, client);
        dispatch({ type: 'UPDATE_CONVERSATION_MEMBERS', payload: { id: conversationId, members: updated.members || [] } });
        toast.success('Member removed');
      } catch (err: any) {
        console.error('Error removing group member:', err);
        toast.error(err.message || 'Failed to remove member');
      }
    },
    []
  );

  // ---- Load group members on demand ----
  const loadGroupMembers = useCallback(
    async (conversationId: string) => {
      const client = clientRef.current;
      if (!client) return;

      try {
        const convo = await getConversation(conversationId);
        if (!convo) return;

        let rawMembers: any[] = [];
        try {
          rawMembers = typeof convo.members === 'function'
            ? await convo.members()
            : Array.isArray(convo.members) ? convo.members : [];
        } catch { return; }

        const memberAddresses = await Promise.all(
          rawMembers.map(async (m: any) => {
            const inboxId = m.inboxId || m;
            return resolveInboxIdToAddress(client, inboxId);
          })
        );

        dispatch({ type: 'UPDATE_CONVERSATION_MEMBERS', payload: { id: conversationId, members: memberAddresses } });

        // Check if current user was removed from or re-added to the group
        const myAddress = chatAddressRef.current?.toLowerCase();
        if (myAddress && memberAddresses.length > 0) {
          const stillMember = memberAddresses.some(
            (a) => a.toLowerCase() === myAddress
          );
          if (!stillMember) {
            dispatch({ type: 'MARK_REMOVED_FROM_GROUP', payload: conversationId });
          } else {
            // Clear removal flag in case user was re-added (or stream event was missed)
            dispatch({ type: 'CLEAR_REMOVED_FROM_GROUP', payload: conversationId });
            convoMapCacheRef.current.delete(conversationId);
          }
        }
      } catch (err) {
        console.error('Error loading group members:', err);
      }
    },
    [getConversation]
  );

  // ---- Check if current user is admin/superAdmin of a group ----
  const isGroupAdmin = useCallback(
    async (conversationId: string): Promise<boolean> => {
      const client = clientRef.current;
      if (!client) return false;

      try {
        const convo = await getConversation(conversationId);
        if (!convo) return false;

        const myInboxId = client.inboxId;
        if (!myInboxId) return false;

        const admins: string[] = convo.admins || [];
        const superAdmins: string[] = convo.superAdmins || [];
        return admins.includes(myInboxId) || superAdmins.includes(myInboxId);
      } catch {
        return false;
      }
    },
    [getConversation]
  );

  // ---- Start message stream when client is ready ----
  useEffect(() => {
    const client = state.client;
    if (!client || !state.isInitialized) return;

    let cancelled = false;

    async function startStreams() {
      // Use SDK built-in retry: retryAttempts + retryDelay handle reconnection natively.
      const msgStream = await client!.conversations.streamAllMessages({
        consentStates: [ConsentState.Unknown, ConsentState.Allowed],
        retryAttempts: 50,
        retryDelay: 5000,
        onError: (err) => console.warn('[XMTP] Message stream error:', err.message),
        onFail: () => console.error('[XMTP] Message stream permanently failed'),
      });
      streamRef.current = msgStream;

      for await (const msg of msgStream) {
        if (cancelled) break;
        const myInboxId = client!.inboxId || '';

        // Handle group membership change messages for real-time removal/re-add detection
        const contentTypeId = msg.contentType?.typeId || '';
        if (contentTypeId === 'group_membership_change' || isSystemContent(msg.content)) {
          const convId = msg.conversationId || '';
          if (convId && msg.content) {
            const memberChange = msg.content as any;
            const removed: any[] = memberChange.removedInboxes || [];
            const added: any[] = memberChange.addedInboxes || [];
            const wasRemoved = removed.some(
              (r: any) => (r.inboxId || r).toLowerCase() === myInboxId.toLowerCase()
            );
            const wasAdded = added.some(
              (a: any) => (a.inboxId || a).toLowerCase() === myInboxId.toLowerCase()
            );
            if (wasRemoved) {
              dispatch({ type: 'MARK_REMOVED_FROM_GROUP', payload: convId });
            } else if (wasAdded) {
              dispatch({ type: 'CLEAR_REMOVED_FROM_GROUP', payload: convId });
              // Invalidate cached conversation object so sends use a fresh MLS session
              convoMapCacheRef.current.delete(convId);
            }
          }
          continue;
        }

        const message = toMessage(msg, myInboxId);

        // Skip other XMTP system/internal messages
        if (!message) continue;
        if (!message.conversationId) continue;

        // Resolve sender inbox ID to Ethereum address before dispatching/caching
        try {
          const resolved = await resolveInboxIdToAddress(client!, message.senderAddress);
          if (resolved && resolved !== message.senderAddress) {
            message.senderAddress = resolved;
          }
        } catch { /* non-critical — keep inbox ID */ }

        // If this message is for a conversation not yet in state (e.g. new DM
        // from an unknown user), fetch it from the SDK and add it to the sidebar.
        const knownIds = (stateConvosRef.current || []).map((c: ConversationData) => c.id);
        if (!knownIds.includes(message.conversationId)) {
          const textPreview = getMessagePreview(message, msg);
          // Add a placeholder immediately so incoming chats are never hidden
          // while background sync catches up.
          const placeholderConvo: ConversationData = {
            id: message.conversationId,
            topic: message.conversationId,
            peerAddress: message.senderAddress,
            isGroup: false,
            groupName: undefined,
            members: undefined,
            createdAt: message.sentAt,
            lastMessageText: textPreview,
            lastMessageTime: message.sentAt,
            lastMessageSender: message.senderAddress,
          };
          dispatch({ type: 'ADD_CONVERSATION', payload: placeholderConvo });

          if (chatAddress) {
            const cachedConvos = getCachedConversations(chatAddress) || [];
            const dupById = cachedConvos.some((c) => c.id === message.conversationId);
            const dupByPeer = message.senderAddress && cachedConvos.some(
              (c) => !c.isGroup && c.peerAddress && c.peerAddress.toLowerCase() === message.senderAddress.toLowerCase()
            );
            if (!dupById && !dupByPeer) {
              setCachedConversations(chatAddress, [placeholderConvo, ...cachedConvos]);
            }
          }

          try {
            await client!.conversations.sync();
            const allConvos = await client!.conversations.list(LIST_ALL_CONSENT);
            convoMapCacheRef.current = new Map(allConvos.map((c: any) => [c.id || c.topic, c]));
            const rawConvo = allConvos.find((c: any) => (c.id || c.topic) === message.conversationId);
            if (rawConvo) {
              const convoData = await toConversationLight(rawConvo, client!);
              dispatch({ type: 'ADD_CONVERSATION', payload: convoData });
            }
          } catch {
            // Non-critical — placeholder already added
          }
        }

        dispatch({
          type: 'APPEND_MESSAGE',
          payload: { conversationId: message.conversationId, message },
        });

        // Append to message cache (with deduplication)
        const existingCache = getCachedMessages(message.conversationId) || [];
        if (!existingCache.some((m) => m.id === message.id)) {
          existingCache.push(message);
          setCachedMessages(message.conversationId, existingCache);
        }

        // Update conversation's last message
        const textPreview = getMessagePreview(message, msg);
        dispatch({
          type: 'UPDATE_CONVERSATION_LAST_MESSAGE',
          payload: {
            id: message.conversationId,
            text: textPreview,
            time: message.sentAt,
            sender: message.senderAddress,
          },
        });

        // Update conversation cache with new preview
        if (chatAddress) {
          updateConversationCachePreview(chatAddress, message.conversationId, textPreview, message.sentAt, message.senderAddress);
        }

        // Increment unread if not the active conversation and not sent by me
        // Use ref to always get the current activeConversationId (avoids stale closure)
        if (
          message.conversationId !== activeConvoRef.current &&
          !message.isSentByMe
        ) {
          dispatch({ type: 'INCREMENT_UNREAD', payload: message.conversationId });
        }

        // Schedule a debounced backup so incoming messages are persisted
        (window as any).__horusScheduleBackup?.();
        (window as any).__horusScheduleArchiveSync?.();
      }
    }

    async function startConvoStream() {
      // Note: conversations.stream() does not support consentStates filtering
      // in the current SDK version, unlike streamAllMessages().
      // New conversations from unknown senders are discovered via welcomePoll instead.
      const cStream = await client!.conversations.stream({
        retryAttempts: 50,
        retryDelay: 5000,
        onError: (err) => console.warn('[XMTP] Conversation stream error:', err.message),
        onFail: () => console.error('[XMTP] Conversation stream permanently failed'),
      });
      convoStreamRef.current = cStream;

      for await (const convo of cStream) {
        if (cancelled) break;
        const convoData = await toConversationLight(convo, client!);
        // Update conversation object cache
        convoMapCacheRef.current.set(convoData.id, convo);
        dispatch({ type: 'ADD_CONVERSATION', payload: convoData });
      }
    }

    // Light sync: refresh conversation list and discover new conversations.
    // For newly found conversations, also loads their latest message preview.
    async function lightSync() {
      // Two-phase sync:
      // 1. conversations.sync() — fetches new welcome messages and conversations from network
      // 2. conversations.syncAll() — processes welcomes and syncs messages for all conversations
      // Note: syncAll() is called WITHOUT consent filtering so it processes ALL welcome messages.
      // Consent filtering is applied later at the list() level for display.
      // Step 1: conversations.sync() — fetch welcome messages (lightweight, rarely fails)
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          await client!.conversations.sync();
          break;
        } catch {
          if (attempt < 3) await new Promise(r => setTimeout(r, 1000 * attempt));
        }
      }

      // Step 2: syncAll() — sync messages for all conversations (may fail on corrupted groups)
      // Do NOT bail if this fails — continue with conversation listing and active-convo refresh.
      // Retry once for transient failures.
      let lightSyncAllOk = false;
      for (let attempt = 1; attempt <= 2; attempt++) {
        try {
          await Promise.race([
            client!.conversations.syncAll(SYNC_ALL_CONSENT),
            new Promise((_, reject) => setTimeout(() => reject(new Error('syncAll timeout')), 30000)),
          ]);
          lightSyncAllOk = true;
          break;
        } catch (err: any) {
          console.warn(`[XMTP lightSync] syncAll attempt ${attempt}/2 failed:`, err?.message);
          if (attempt < 2) await new Promise(r => setTimeout(r, 1000));
        }
      }
      if (!lightSyncAllOk) {
        // syncAll failed — fall back to syncing the active conversation individually
        const activeId = activeConvoRef.current;
        if (activeId) {
          const activeConvo = convoMapCacheRef.current.get(activeId);
          if (activeConvo) {
            try { await activeConvo.sync(); } catch { /* non-critical */ }
          }
        }
      }

      try {
        const allConvos = await client!.conversations.list(LIST_ALL_CONSENT);

        convoMapCacheRef.current = new Map(allConvos.map((c: any) => [c.id || c.topic, c]));

        // Check for new conversations not yet in state
        const existingIds = new Set(
          (stateConvosRef.current || []).map((c: ConversationData) => c.id)
        );
        const myInboxId = client!.inboxId || '';
        const newConvoIds: string[] = [];

        for (const convo of allConvos) {
          const id = convo.id || (convo as any).topic;
          if (!existingIds.has(id)) {
            newConvoIds.push(id);
            const convoData = await toConversationLight(convo, client!);

            // Load latest message preview for the new conversation
            try {
              await convo.sync().catch(() => { });
              const recentMsgs = await convo.messages({ limit: BigInt(5), direction: SortDirection.Ascending });
              const parsed = recentMsgs
                .map((m: any) => toMessage(m, myInboxId))
                .filter((m: any): m is ChatMessage => m !== null);

              if (parsed.length > 0) {
                const latest = parsed[parsed.length - 1];
                convoData.lastMessageText = typeof latest.content === 'string'
                  ? latest.content.slice(0, 100)
                  : latest.content?.isVoiceNote ? '\uD83C\uDFA4 Voice message' : '[Attachment]';
                convoData.lastMessageTime = latest.sentAt;
                convoData.lastMessageSender = latest.senderAddress;
              }
            } catch { /* non-critical */ }

            dispatch({ type: 'ADD_CONVERSATION', payload: convoData });

            // Persist to cache so it survives refresh (with peer-address dedup)
            if (chatAddress) {
              const currentConvos = stateConvosRef.current || [];
              const peerDup = !convoData.isGroup && convoData.peerAddress && currentConvos.some(
                (c) => !c.isGroup && c.peerAddress && c.peerAddress.toLowerCase() === convoData.peerAddress.toLowerCase()
              );
              if (!peerDup) {
                setCachedConversations(chatAddress, [...currentConvos, convoData]);
              }
            }
          }
        }

        // After syncAll + discovering new conversations, refresh messages
        // for ALL existing conversations to catch anything the stream missed.
        // This is the 60-second comprehensive sweep that complements the
        // 10-second welcomePoll (which only checks 3 conversations per cycle).
        const myInboxForRefresh = client!.inboxId || '';
        for (const convo of allConvos) {
          const convoId = convo.id || (convo as any).topic;
          try {
            await convo.sync().catch(() => {});
            const msgs = await convo.messages({ limit: BigInt(5), direction: SortDirection.Ascending });
            const parsed = msgs
              .map((m: any) => toMessage(m, myInboxForRefresh))
              .filter((m: any): m is ChatMessage => m !== null);
            if (parsed.length > 0) {
              const latest = parsed[parsed.length - 1];
              const existing = stateMessagesRef.current?.[convoId] || [];
              const latestOld = existing[existing.length - 1];
              // Update sidebar preview if the latest message changed
              if (!latestOld || latest.id !== latestOld.id) {
                const preview = typeof latest.content === 'string'
                  ? latest.content.slice(0, 100)
                  : latest.content?.isVoiceNote ? '\uD83C\uDFA4 Voice message' : '[Attachment]';
                try {
                  const addr = await resolveInboxIdToAddress(client!, latest.senderAddress);
                  if (addr) latest.senderAddress = addr;
                } catch {}
                dispatch({
                  type: 'UPDATE_CONVERSATION_LAST_MESSAGE',
                  payload: { id: convoId, text: preview, time: latest.sentAt, sender: latest.senderAddress },
                });
                // If messages are loaded for this convo and the stream missed some,
                // dispatch a full refresh so user sees them next time they open it
                if (existing.length > 0) {
                  dispatch({ type: 'INCREMENT_UNREAD', payload: convoId });
                }
              }
            }
          } catch { /* non-critical — skip this conversation */ }
        }

      } catch {
        // Non-critical — will retry on next interval
      }
    }

    // Load cached conversations instantly so the sidebar appears immediately
    const cachedConvos = chatAddress ? getCachedConversations(chatAddress) : null;
    if (cachedConvos && cachedConvos.length > 0) {
      dispatch({ type: 'SET_CONVERSATIONS', payload: cachedConvos });
      resolveHnsDisplayNames(cachedConvos);
    }

    // Load conversations from local OPFS without waiting for network sync.
    // This ensures fast local retrieval even if sync is slow or offline.
    (async () => {
      try {
        const allConvos = await client!.conversations.list(LIST_ALL_CONSENT);
        if (!allConvos || allConvos.length === 0) return;

        convoMapCacheRef.current = new Map(allConvos.map((c: any) => [c.id || c.topic, c]));

        const conversations: ConversationData[] = await Promise.all(
          allConvos.map((convo: any) => toConversationLight(convo, client!))
        );

        // Merge cached previews so last message text/time renders immediately
        if (cachedConvos && cachedConvos.length > 0) {
          const cachedMap = new Map(cachedConvos.map((c) => [c.id, c]));
          conversations.forEach((convo) => {
            const cached = cachedMap.get(convo.id);
            if (!cached) return;
            if (cached.lastMessageText) {
              convo.lastMessageText = cached.lastMessageText;
              convo.lastMessageTime = cached.lastMessageTime;
              convo.lastMessageSender = cached.lastMessageSender;
            }
          });
        }

        conversations.sort((a, b) => {
          const timeA = a.lastMessageTime?.getTime() || a.createdAt.getTime();
          const timeB = b.lastMessageTime?.getTime() || b.createdAt.getTime();
          return timeB - timeA;
        });

        dispatch({ type: 'SET_CONVERSATIONS', payload: conversations });
      } catch {
        // Local load failure is non-critical — network sync will populate
      }
    })();

    // Start streams — SDK handles retry/reconnection natively via StreamOptions.
    // Deduplication in APPEND_MESSAGE handles any overlap.
    startStreams();
    startConvoStream();

    // Recovery sync loop for fresh registrations (no backup restored).
    // After creating a new installation, peers need time to process the new
    // key packages and send welcome messages. This loop runs aggressive syncs
    // for 3 minutes (12 rounds * 15s) to catch conversations as they arrive.
    async function recoverySyncLoop() {
      console.info('[XMTP Recovery] Starting recovery sync loop (3 minutes)...');
      let prevCount = 0;
      let stableRounds = 0;
      for (let i = 0; i < 12; i++) {
        await new Promise(r => setTimeout(r, 15000));
        if (cancelled) break;
        await lightSync();
        const count = (stateConvosRef.current || []).length;
        console.info(`[XMTP Recovery] Round ${i + 1}/12: ${count} conversations.`);
        if (count > 0 && count === prevCount) stableRounds++;
        else stableRounds = 0;
        prevCount = count;
        // Stop early if conversations stabilized for 2 consecutive rounds
        if (stableRounds >= 2) {
          console.info('[XMTP Recovery] Conversation count stabilized, stopping recovery loop.');
          break;
        }
        // Re-send sync request halfway through (peers may have come online)
        if (i === 3) {
          try { await client!.sendSyncRequest(); } catch {}
        }
      }
      isNewRegistrationRef.current = false;
    }

    // Sync from network in parallel with streams.
    // After initial sync, run periodic light re-syncs to discover new conversations
    // from other users (streams may not reliably detect new incoming DMs in MLS).
    syncConversations().then(async () => {
      if (cancelled) return;

      // Merge archived conversations/messages that XMTP may have lost.
      // After a backup restore, the archive likely has newer messages than
      // the (potentially stale) OPFS backup — merge them immediately.
      const justRestored = localStorage.getItem('horus-xmtp-just-restored') === '1';
      if (justRestored) {
        localStorage.removeItem('horus-xmtp-just-restored');
        console.info('[Archive] Post-restore: running immediate archive merge to fill backup gap.');
      }
      if (chatAddress) {
        try {
          const mergeResult = await mergeArchiveIntoState(
            chatAddress,
            stateConvosRef.current || [],
            stateMessagesRef.current || {},
          );
          if (mergeResult) {
            for (const convo of mergeResult.missingConversations) {
              dispatch({ type: 'ADD_CONVERSATION', payload: convo });
            }
            for (const [convoId, msgs] of Object.entries(mergeResult.missingMessages)) {
              dispatch({ type: 'MERGE_MESSAGES', payload: { conversationId: convoId, messages: msgs } });
            }
            if (justRestored) {
              console.info('[Archive] Post-restore merge complete:',
                mergeResult.missingConversations.length, 'conversations,',
                Object.keys(mergeResult.missingMessages).length, 'message sets recovered.');
            }
          }
        } catch (err: any) {
          console.warn('[Archive] Merge-on-restore failed:', err?.message);
        }
      }

      // Single early re-sync to catch conversations missed during initial load
      setTimeout(() => { if (!cancelled) lightSync(); }, 10000);

      // If this is a fresh registration (no backup), run aggressive recovery
      if (isNewRegistrationRef.current) {
        recoverySyncLoop();
      }
    });

    // Fast poll: runs every 10 seconds to discover new conversations AND
    // refresh messages for conversations (fallback when stream misses messages).
    // Round-robin index for rotating through non-active conversations
    let welcomePollRotation = 0;
    async function welcomePoll() {
      try {
        // Step 1: Discover new conversations
        await client!.conversations.sync();
        const allConvos = await client!.conversations.list(LIST_ALL_CONSENT);
        const existingIds = new Set(
          (stateConvosRef.current || []).map((c: ConversationData) => c.id)
        );

        let foundNew = false;
        for (const convo of allConvos) {
          const id = convo.id || (convo as any).topic;
          if (!existingIds.has(id)) {
            foundNew = true;
            convoMapCacheRef.current.set(id, convo);
            const convoData = await toConversationLight(convo, client!);
            dispatch({ type: 'ADD_CONVERSATION', payload: convoData });
          }
        }

        if (foundNew) {
          await lightSync();
        }

        // Step 2: Sync + reload messages for conversations.
        // Run syncAll() first to ensure all MLS operations (commits, proposals,
        // messages) are processed. Individual convo.sync() can silently skip
        // messages when the MLS epoch is stale — syncAll handles this correctly.
        try {
          await client!.conversations.syncAll(SYNC_ALL_CONSENT);
        } catch { /* syncAll failure is non-critical — fall back to individual sync */ }

        // Refresh conversation objects after syncAll — the cache may hold
        // stale SDK objects whose internal MLS state wasn't updated.
        const freshConvos = await client!.conversations.list(LIST_ALL_CONSENT);
        convoMapCacheRef.current = new Map(freshConvos.map((c: any) => [c.id || c.topic, c]));
        const myInboxId = client!.inboxId || '';

        // Helper: refresh messages for a single conversation and dispatch if changed
        const refreshConvoMessages = async (convoId: string, convoObj: any) => {
          try {
            await convoObj.sync().catch(() => {});
            const msgs = await convoObj.messages({ limit: BigInt(MESSAGE_PAGE_SIZE), direction: SortDirection.Ascending });
            const refreshed: ChatMessage[] = msgs
              .map((m: any) => toMessage(m, myInboxId))
              .filter((m: any): m is ChatMessage => m !== null);

            // Resolve sender addresses
            for (const m of refreshed) {
              try {
                const addr = await resolveInboxIdToAddress(client!, m.senderAddress);
                if (addr) m.senderAddress = addr;
              } catch { /* non-critical */ }
            }

            // Only dispatch if message count or latest message changed
            const existing = stateMessagesRef.current?.[convoId] || [];
            const latestNew = refreshed[refreshed.length - 1];
            const latestOld = existing[existing.length - 1];
            if (
              refreshed.length !== existing.length ||
              latestNew?.id !== latestOld?.id
            ) {
              dispatch({ type: 'MERGE_MESSAGES', payload: { conversationId: convoId, messages: refreshed } });
              setCachedMessages(convoId, refreshed);

              // Update sidebar preview
              if (latestNew) {
                const preview = typeof latestNew.content === 'string'
                  ? latestNew.content.slice(0, 100)
                  : latestNew.content?.isVoiceNote ? '\uD83C\uDFA4 Voice message' : '[Attachment]';
                dispatch({
                  type: 'UPDATE_CONVERSATION_LAST_MESSAGE',
                  payload: { id: convoId, text: preview, time: latestNew.sentAt, sender: latestNew.senderAddress },
                });
              }
              return true; // messages changed
            }
          } catch { /* non-critical */ }
          return false;
        }

        // Step 2a: Always refresh the active conversation (highest priority)
        const activeId = activeConvoRef.current;
        if (activeId) {
          const activeConvo = convoMapCacheRef.current.get(activeId);
          if (activeConvo) {
            await refreshConvoMessages(activeId, activeConvo);

            // Check group membership for the active conversation.
            const activeConvoData = (stateConvosRef.current || []).find(
              (c: ConversationData) => c.id === activeId
            );
            if (activeConvoData?.isGroup) {
              const myAddr = chatAddressRef.current?.toLowerCase();
              if (myAddr) {
                try {
                  let rawMembers: any[] = [];
                  try {
                    rawMembers = typeof activeConvo.members === 'function'
                      ? await activeConvo.members()
                      : Array.isArray(activeConvo.members) ? activeConvo.members : [];
                  } catch { /* members() can fail if fully evicted */ }

                  if (rawMembers.length > 0) {
                    const memberAddresses = await Promise.all(
                      rawMembers.map((m: any) =>
                        resolveInboxIdToAddress(client!, m.inboxId || m)
                      )
                    );
                    const stillMember = memberAddresses.some(
                      (a) => a.toLowerCase() === myAddr
                    );

                    if (!stillMember && !activeConvoData.isRemovedFromGroup) {
                      dispatch({ type: 'MARK_REMOVED_FROM_GROUP', payload: activeId });
                    } else if (stillMember && activeConvoData.isRemovedFromGroup) {
                      dispatch({ type: 'CLEAR_REMOVED_FROM_GROUP', payload: activeId });
                      convoMapCacheRef.current.delete(activeId);
                    }
                  } else if (activeConvoData.members && activeConvoData.members.length > 0) {
                    if (!activeConvoData.isRemovedFromGroup) {
                      dispatch({ type: 'MARK_REMOVED_FROM_GROUP', payload: activeId });
                    }
                  }
                } catch { /* non-critical — will retry on next poll */ }
              }
            }
          }
        }

        // Step 2b: Rotate through non-active conversations to catch messages
        // the stream missed. Check up to 3 conversations per poll cycle,
        // round-robin via pollRotationIndex to cover all conversations over time.
        const allConvoIds = Array.from(convoMapCacheRef.current.keys()).filter(id => id !== activeId);
        if (allConvoIds.length > 0) {
          const BATCH_SIZE = 3;
          const startIdx = welcomePollRotation % allConvoIds.length;
          welcomePollRotation += BATCH_SIZE;
          for (let i = 0; i < Math.min(BATCH_SIZE, allConvoIds.length); i++) {
            const idx = (startIdx + i) % allConvoIds.length;
            const convoId = allConvoIds[idx];
            const convoObj = convoMapCacheRef.current.get(convoId);
            if (convoObj) {
              await refreshConvoMessages(convoId, convoObj);
            }
          }
        }
      } catch { /* non-critical */ }
    }

    // Welcome poll every 10 seconds — lightweight check for new conversations
    const welcomePollInterval = setInterval(() => {
      if (!cancelled) welcomePoll();
    }, 10000);

    // Full lightSync every 60 seconds for message preview enrichment
    const lightSyncInterval = setInterval(() => {
      if (!cancelled) lightSync();
    }, 60000);

    // ---- Backup helpers: sync OPFS state then upload to Firebase ----

    // Sync all conversations before backup to ensure OPFS has the latest messages.
    // Without this, backup may capture a stale database snapshot.
    async function syncThenBackup() {
      const signer = pendingSignerRef.current;
      if (!chatAddress || !signer) return;
      try {
        // Ensure OPFS database is up-to-date before exporting
        await client!.conversations.sync();
        try {
          await client!.conversations.syncAll(SYNC_ALL_CONSENT);
        } catch { /* corrupted group — proceed with best-effort backup */ }
        await backupXmtpDatabase(chatAddress, signer);
      } catch (err: any) {
        console.warn('[XMTP Backup] syncThenBackup failed:', err?.message);
      }
    }

    // Debounced backup: coalesces rapid triggers (e.g. burst of incoming messages)
    // into a single backup after 10 seconds of quiet.
    let backupDebounceTimer: ReturnType<typeof setTimeout> | null = null;
    function scheduleBackup() {
      if (backupDebounceTimer) clearTimeout(backupDebounceTimer);
      backupDebounceTimer = setTimeout(() => {
        if (!cancelled) syncThenBackup();
      }, 10000);
    }

    // Expose scheduleBackup so sendMessage and stream handlers can trigger it
    (window as any).__horusScheduleBackup = scheduleBackup;

    // Periodic background backup: persist OPFS database to Firebase.
    // Use shorter interval (30s) when browser denied persistent storage,
    // since OPFS data may be evicted at any time.
    const storagePersisted = localStorage.getItem('horus-xmtp-storage-persisted') !== '0';
    const backupIntervalMs = storagePersisted ? 60 * 1000 : 30 * 1000;
    const backupInterval = setInterval(() => {
      if (!cancelled) syncThenBackup();
    }, backupIntervalMs);

    // Backup on page unload: persist messages to localStorage (synchronous, reliable)
    // and attempt async Firebase backup as best-effort.
    function onBeforeUnload() {
      // Synchronous localStorage write — always completes before tab closes
      try {
        const addr = chatAddress || chatAddressRef.current;
        if (addr && stateConvosRef.current?.length) {
          setCachedConversations(addr, stateConvosRef.current);
        }
        const msgs = stateMessagesRef.current || {};
        for (const [convoId, convoMsgs] of Object.entries(msgs)) {
          if (convoMsgs.length > 0) setCachedMessages(convoId, convoMsgs);
        }
      } catch { /* cache write is best-effort */ }

      // Async Firebase backup — browser may kill this, but it often completes for small payloads
      const signer = pendingSignerRef.current;
      if (chatAddress && signer) {
        backupXmtpDatabase(chatAddress, signer).catch(() => {});
      }
    }

    // Backup on visibility change: when user switches tabs or minimizes,
    // trigger a full sync+backup so the latest state is persisted.
    async function onVisibilityChange() {
      if (document.visibilityState === 'hidden' && !cancelled) {
        // Serialize: complete OPFS backup before starting archive sync
        // to avoid concurrent XMTP client / Firebase access
        await syncThenBackup();
        doArchiveSync();
      }
    }

    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('visibilitychange', onVisibilityChange);

    // ---- Message archive sync: encrypted per-conversation backup to Firebase ----

    async function doArchiveSync() {
      if (!chatAddress) return;
      try {
        await syncMessageArchive(
          chatAddress,
          stateConvosRef.current || [],
          stateMessagesRef.current || {},
          undefined,
          pendingSignerRef.current || undefined,
        );
      } catch (err: any) {
        console.warn('[Archive] Sync failed:', err?.message);
      }
    }

    let archiveDebounceTimer: ReturnType<typeof setTimeout> | null = null;
    function scheduleArchiveSync() {
      if (archiveDebounceTimer) clearTimeout(archiveDebounceTimer);
      archiveDebounceTimer = setTimeout(() => {
        if (!cancelled) doArchiveSync();
      }, 15000);
    }

    (window as any).__horusScheduleArchiveSync = scheduleArchiveSync;

    // Periodic archive sync every 3 minutes
    const archiveInterval = setInterval(() => {
      if (!cancelled) doArchiveSync();
    }, 3 * 60 * 1000);

    return () => {
      cancelled = true;
      if (backupDebounceTimer) clearTimeout(backupDebounceTimer);
      if (archiveDebounceTimer) clearTimeout(archiveDebounceTimer);
      delete (window as any).__horusScheduleBackup;
      delete (window as any).__horusScheduleArchiveSync;
      clearInterval(backupInterval);
      clearInterval(archiveInterval);
      clearInterval(welcomePollInterval);
      clearInterval(lightSyncInterval);
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      streamRef.current?.return?.();
      convoStreamRef.current?.return?.();
    };
  }, [state.client, state.isInitialized]);

  // ---- Cleanup on logout ----
  useEffect(() => {
    if (!isLoggedIn && (state.isInitialized || state.pendingClient)) {
      closeXmtpClient(state.client);
      if (state.pendingClient) {
        try { state.pendingClient.close(); } catch { /* ignore */ }
      }
      pendingSignerRef.current = null;
      setStoredSigner(null);
      streamRef.current?.return?.();
      convoStreamRef.current?.return?.();
      autoInitAttemptedRef.current = false;
      inboxIdAddressCache.clear();
      convoMapCacheRef.current = new Map();
      invalidateManifestCache();

      // Clear per-user caches from localStorage to prevent data leakage
      // on shared devices. Preserve DB key + backup key (needed for restore).
      if (chatAddress) {
        const addr = chatAddress.toLowerCase();
        localStorage.removeItem('horus-xmtp-conversations-' + addr);
        localStorage.removeItem('horus-xmtp-session-ok-' + addr);
        // Clear per-conversation message caches
        try {
          const keys = Object.keys(localStorage);
          for (const k of keys) {
            if (k.startsWith('horus-xmtp-msgs-')) localStorage.removeItem(k);
            if (k.startsWith('horus-xmtp-lastread-')) localStorage.removeItem(k);
          }
        } catch { /* non-critical */ }
      }

      dispatch({ type: 'RESET' });
    }
  }, [isLoggedIn]);

  // ---- Context values ----
  const clientContextValue: ChatClientContextValue = {
    isInitialized: state.isInitialized,
    isInitializing: state.isInitializing,
    initError: state.initError,
    initProgress: state.initProgress,
    hasCloudBackup: state.hasCloudBackup,
    chatAddress,
    initializeChat,
    resetChat,
    showInstallationManager: state.showInstallationManager,
    pendingClient: state.pendingClient,
    pendingInboxId: state.pendingInboxId,
    completeRegistration,
    dismissInstallationManager,
    revokeInstallations,
    forceRevokeAllAndReset,
  };

  const dataContextValue: ChatDataContextValue = {
    conversations: state.conversations,
    activeConversationId: state.activeConversationId,
    messages: state.messages,
    loadingMessages: state.loadingMessages,
    hasMoreMessages: state.hasMoreMessages,
    unreadCounts: state.unreadCounts,
    totalUnread: state.totalUnread,
    searchQuery: state.searchQuery,
    isSyncing: state.isSyncing,
    conversationFilter: state.conversationFilter,
    lastReadAt: lastReadAtRef.current,
    dispatch,
    sendMessage,
    loadMessages,
    loadOlderMessages,
    selectConversation,
    createDm,
    createGroup,
    addGroupMembers,
    removeGroupMember,
    loadGroupMembers,
    isGroupAdmin,
    syncConversations,
  };

  return (
    <ChatClientContext.Provider value={clientContextValue}>
      <ChatDataContext.Provider value={dataContextValue}>
        {children}
      </ChatDataContext.Provider>
    </ChatClientContext.Provider>
  );
}

export const useChatClient = () => useContext(ChatClientContext);
export const useChatData = () => useContext(ChatDataContext);
